import { useEffect, useState, useCallback } from 'react';
import { bannerApi, uploadApi } from '../api/client';
import type { Banner } from '@bosamie/shared';
import {
  showToast, ConfirmDialog, Drawer, ImageUploader,
  TableSkeleton, EmptyState, formatPrice,
} from '../components/ui';

export function Banners() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [sort, setSort] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<Banner | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bannerApi.list();
      setBanners(data);
    } catch (e) {
      showToast('加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => {
    setEditId(null);
    setImageUrl('');
    setSort(banners.length);
    setDrawerOpen(true);
  };

  const openEdit = (banner: Banner) => {
    setEditId(banner.id);
    setImageUrl(banner.imageUrl);
    setSort(banner.sort);
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!imageUrl) {
      showToast('请上传轮播图');
      return;
    }
    try {
      if (editId) {
        await bannerApi.update(editId, { imageUrl, sort });
        showToast('保存成功');
      } else {
        await bannerApi.create({ imageUrl, sort });
        showToast('创建成功');
      }
      setDrawerOpen(false);
      fetch();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '保存失败');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await bannerApi.delete(deleteTarget.id);
      showToast('删除成功');
      setDeleteTarget(null);
      fetch();
    } catch {
      showToast('删除失败');
    }
  };

  const handleMove = async (index: number, dir: 'up' | 'down') => {
    const swapIndex = dir === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= banners.length) return;
    const newOrder = [...banners];
    [newOrder[index], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[index]];
    setBanners(newOrder);
    try {
      await bannerApi.reorder(newOrder.map((b) => b.id));
    } catch {
      showToast('排序失败');
      fetch();
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">轮播管理</div>
        <button className="btn btn-primary" onClick={openCreate}>+ 新增轮播</button>
      </div>

      {loading ? (
        <TableSkeleton rows={3} cols={3} />
      ) : banners.length === 0 ? (
        <EmptyState title="暂无轮播图" action={
          <button className="btn btn-primary" onClick={openCreate}>+ 新增轮播</button>
        } />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>轮播图</th>
                <th>顺序</th>
                <th style={{ textAlign: 'right' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {banners.map((banner, i) => (
                <tr key={banner.id}>
                  <td>
                    <div style={{ width: 128, height: 76, borderRadius: 6, overflow: 'hidden' }}>
                      <img src={banner.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  </td>
                  <td>{i + 1}</td>
                  <td>
                    <div className="actions">
                      <button className="btn-icon" onClick={() => handleMove(i, 'up')} disabled={i === 0} title="上移">↑</button>
                      <button className="btn-icon" onClick={() => handleMove(i, 'down')} disabled={i === banners.length - 1} title="下移">↓</button>
                      <button className="btn-icon" onClick={() => openEdit(banner)} title="编辑">✎</button>
                      <button className="btn-icon danger" onClick={() => setDeleteTarget(banner)} title="删除">🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer */}
      {drawerOpen && (
        <Drawer
          title={editId ? '编辑轮播' : '新增轮播'}
          onClose={() => setDrawerOpen(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setDrawerOpen(false)}>取消</button>
              <button className="btn btn-primary" onClick={handleSave}>保存</button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">轮播大图 <span className="req">*</span></label>
            <ImageUploader
              files={imageUrl ? [{ url: imageUrl }] : []}
              onAdd={(url) => setImageUrl(url)}
              onRemove={() => setImageUrl('')}
            />
          </div>
          <div className="form-group">
            <label className="form-label">顺序</label>
            <input
              type="number"
              className="form-input"
              value={sort}
              onChange={(e) => setSort(Number(e.target.value))}
              min={0}
            />
          </div>
        </Drawer>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <ConfirmDialog
          title="删除轮播图"
          danger
          body="确定要删除这张轮播图吗？此操作不可撤销。"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
